import { createSlice, combineReducers, PayloadAction } from "@reduxjs/toolkit";

import {
  createClearableIndexedRequestReducer,
  reduceReducers,
  createIndexedRequestReducerFromThunk,
  createRequestReducerFromThunk,
} from "@skydio/redux_util/src";

import { acceptOrganizationInvite, removeUserFromOrganization } from "../organizations/asyncThunks";
import { fetchUsers, fetchUser, createUser, updateUser } from "./asyncThunks";

import { APIPagination } from "../pagination/types";
import { UsersPrimaryState, User, APIUser, UsersMap, UserUpdate } from "./types";
import * as pbtypes_tools_cloud_api_enums_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";

const defaultPagination: APIPagination = {
  maxPerPage: 25,
  currentPage: 1,
  totalPages: 1,
};

const initialState: UsersPrimaryState = {
  users: {},
  dispatchTimeout: null,
  requestedIds: [],
  addModalOpen: false,
  pagination: defaultPagination,
};

export const initialUserState: User = {
  uuid: "",
  email: "",
  firstName: "",
  lastName: "",
  displayName: "",
  active: false,
  enabled: false,
  emailStatus: "",
  expiry: undefined,
  groups: [],
  organizationId: "",
  organizationName: "",
  organizationPermission: 0,
  organizationGroups: [],
  organizationNotes: "",
  modified: {},
  organizationFlightCount: 0,
  resourceGroupLinksList: [],
  loginDefault: false,
};

export interface FieldUpdatePayload {
  uuid: string;
  name: keyof UserUpdate;
  value: UserUpdate[keyof UserUpdate] | null;
}

const updateUserState = (state: UsersMap, user: APIUser) => {
  if (!(user.uuid in state)) {
    state[user.uuid] = { ...initialUserState };
  }
  Object.assign(state[user.uuid]!, user, {
    groups: user.groupsList ? user.groupsList.map(group => group.name) : [],
    orgGroups: user.organizationGroupsList
      ? user.organizationGroupsList.map(group => group.name)
      : [],
    organizationFlightCount:
      user.organizationFlightCount || state[user.uuid]!.organizationFlightCount,
  });
};

const { actions, reducer: primaryReducer } = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsersQueryTimeout(state, { payload }: PayloadAction<number>) {
      state.dispatchTimeout = payload;
    },
    clearUsersQueryTimeout(state) {
      state.dispatchTimeout = null;
    },
    updateUserField(state, { payload }: PayloadAction<FieldUpdatePayload>) {
      const user = state.users[payload.uuid];
      if (payload.value === null) {
        delete user!.modified[payload.name];
      } else {
        user!.modified = {
          ...user!.modified,
          [payload.name]: payload.value,
        };
      }
    },
    clearUserModifications(state, { payload }: PayloadAction<string>) {
      state.users[payload]!.modified = {};
    },
  },
  extraReducers: builder =>
    builder
      .addCase(fetchUsers.fulfilled, (state, { payload }) => {
        payload.usersList.forEach(user => {
          updateUserState(state.users, user);
        });
        state.requestedIds = payload.usersList.map(({ uuid }) => uuid);
        state.pagination = {
          maxPerPage: payload.pagination!.maxPerPage,
          currentPage: payload.pagination!.currentPage,
          totalPages: payload.pagination!.totalPages,
        };
      })
      .addCase(fetchUsers.rejected, state => {
        state.requestedIds = [];
      })
      .addCase(fetchUser.fulfilled, (state, { payload }) => {
        updateUserState(state.users, payload);
      })
      .addCase(createUser.fulfilled, (state, { payload }) => {
        updateUserState(state.users, payload);
      })
      .addCase(updateUser.fulfilled, (state, { payload }) => {
        updateUserState(state.users, payload);
        state.users[payload.uuid]!.modified = {};
      }),
});

const { clearAction: clearUserRequest, clearableReducer } = createClearableIndexedRequestReducer(
  reduceReducers(
    createIndexedRequestReducerFromThunk(fetchUser),
    createIndexedRequestReducerFromThunk(createUser, "email"),
    createIndexedRequestReducerFromThunk(updateUser, "uuid")
  ),
  "users"
);

const reducer = combineReducers({
  state: primaryReducer,
  requests: combineReducers({
    users: createRequestReducerFromThunk(fetchUsers),
    user: clearableReducer,
    orgRemovals: createIndexedRequestReducerFromThunk(removeUserFromOrganization, "email"),
    acceptedOrgInvite: createRequestReducerFromThunk(acceptOrganizationInvite),
  }),
});
export type UsersState = ReturnType<typeof reducer>;

export const userActions = { ...actions, clearUserRequest };
export default reducer;
